import { ThunkAction } from 'redux-thunk';
import * as Actions from './actions';
import { AnyAction } from 'redux';
import { AppState } from '../../store';
import moment from 'moment';
import { homehubUserApiPath, v2BaseApiPath } from '../../../config/baseConfig';
import { SumamoruAdditionalServices, guideAppType } from '../property/types';
import { isContractorOnly, isOccupantOnly } from '../invitation/operations';
import { ApplicantType, getApplicantText } from '../invitation/types';

export interface ActivationContext {
  organizationId: string;
  usageInfoId: string;
  applicantType: ApplicantType;
}

/** 対象の入居情報が Inviting/Sending でないときに homehub が返すコード。= 既にアクティベート済み */
const ALREADY_ACTIVATED_CODE = 'not-found-any-space-usage-and-owners';

/**
 * 入居者本人の操作なので、takuty の完了API経由ではなく本人のアクセストークンで homehub を直接呼ぶ
 * (完了API経由だと takuty が本人に代わって主張する形になる)
 */
const activateOccupant = async (param: {
  organizationId: string;
  peopleId: string;
  usageId: string;
  accessToken: string;
}): Promise<boolean> => {
  const query = new URLSearchParams({
    organization_id: param.organizationId,
    people_id: param.peopleId
  });
  const res = await fetch(
    `${homehubUserApiPath}/organizations/people/activate?${query.toString()}`,
    {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'x-api-key': param.accessToken
      },
      body: JSON.stringify({ accept_space_usage_ids: [param.usageId] }),
      mode: 'cors'
    }
  );
  if (res.status === 200) {
    return true;
  }
  // 既にアクティベート済みなら成功扱い。移行期間中は完了API側も activate するため二重になり、
  // 招待URLで再訪して再実行したときも同じ応答になる
  if (res.status === 400) {
    const json = await res.json().catch(() => undefined);
    return json && json.code === ALREADY_ACTIVATED_CODE;
  }
  return false;
};

export const submitParamater = (
  activation: ActivationContext
): ThunkAction<Promise<void>, AppState, {}, AnyAction> => async (
  dispatch,
  getState
) => {
  try {
    dispatch(Actions.submitParamater());

    const formParam = getState().applyForm.parameters;
    const email = getState().user.email;
    const access_token = getState().user.access_token;
    const refresh_token = getState().user.refresh_token;
    const user_id = getState().user.user_id;
    const propParam = getState().property.property;
    const phoneNumber = getState().user.phoneNumber;
    const method = 'POST';

    const isContractorUser = isContractorOnly(activation.applicantType);
    const isOccupantUser = isOccupantOnly(activation.applicantType);

    // 契約者のみはBKPアカウント連携を行わないため access_token を持たない。
    // 認証ヘッダを付けられないので アカウント登録もスキップする
    const headers: { [key: string]: string } = isContractorUser
      ? { Accept: 'application/json', 'Content-Type': 'application/json' }
      : {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'x-api-key': access_token
        };
    let body = '';
    let bkpPersonaId: string | undefined;

    if (!isContractorUser) {
      const homeHubUserEndpoint = `${v2BaseApiPath}/accounts`;
      body = JSON.stringify({
        bkpUserId: user_id,
        name: `${formParam.family_name} ${formParam.first_name}`,
        ...(email ? { email } : {}),
        ...(phoneNumber ? { phoneNumber } : {})
      });
      const accountRes = await fetch(homeHubUserEndpoint, {
        method,
        headers,
        body,
        mode: 'cors'
      });

      const accountSucceeded =
        accountRes.status === 200 || accountRes.status === 201;

      if (!accountSucceeded) {
        dispatch(
          Actions.failedToSubmitParamater(
            accountRes.status === 401 ? 'unauthorized' : 'internal'
          )
        );
        return;
      }

      // BKPログイン応答にも personaId は含まれるが、状態をクッキーから復元する経路では
      // 持ち回れないため homehub に返させる
      const accountJson = await accountRes.json().catch(() => undefined);
      bkpPersonaId = accountJson && accountJson.personaId;
      if (!bkpPersonaId) {
        dispatch(Actions.failedToSubmitParamater('internal'));
        return;
      }
    }

    const endpoint = `${v2BaseApiPath}/application/create`;

    const { isSameEmail } = getState().applyForm.parameters;
    const { isCoodinatedGasStartDate } = getState().applyForm.parameters;

    let formEmail = formParam.email;

    if (isSameEmail) {
      formEmail = email;
    }

    // 申込者種別ごとに、共通項目へ contractor / occupant を足す
    // 兼任: 両方 / 入居者: occupant のみ / 契約者: contractor のみ
    const applicationTarget = {
      ...(isContractorUser ? {} : { refresh_token: refresh_token }),
      ...(isContractorUser
        ? {
            contract_info_id: propParam.contractInfoId,
            people_id: propParam.peopleId
          }
        : {}),
      applicant_type: getApplicantText(activation.applicantType),
      organization_id: activation.organizationId,
      property_id: propParam.allSpaceId,
      contract_id: propParam.contractId,
      property_no: propParam.sumamoruCode,
      building_name: propParam.buildingName,
      postal_code: propParam.postCode,
      address1:
        propParam.address ||
        propParam.prefecture + propParam.city + propParam.afterCity,
      address2: propParam.buildingName + propParam.name,
      ...(isContractorUser
        ? {}
        : {
            move_plan_date: moment(propParam.occupyScheduledDate).format(
              'YYYY-MM-DD'
            )
          }),
      ...(propParam.viewnStartDate
        ? { viewn_start_date: propParam.viewnStartDate }
        : {}),
      has_electric_contract: propParam.hasElectricContract,
      sumamoru_project_type: propParam.sumamoruProjectType,
      install_app_name: guideAppType(propParam.sumamoruInstallAppTypes),
      sumamoruAdditionalServices:
        propParam.sumamoruAdditionalServices &&
        propParam.sumamoruAdditionalServices.filter(
          (service): service is SumamoruAdditionalServices =>
            Object.values(SumamoruAdditionalServices).includes(
              service as SumamoruAdditionalServices
            )
        )
    };

    const applicantProfile = {
      family_name: formParam.family_name,
      first_name: formParam.first_name,
      family_name_kana: formParam.family_name_kana,
      first_name_kana: formParam.first_name_kana,
      email: formEmail,
      phone_number: formParam.phone_number,
      phone_number_after_move: formParam.phone_number,
      ...(isContractorUser
        ? {}
        : {
            birthday: formParam.birthday,
            sex: parseInt(formParam.gender, 10)
          })
    };

    const contractor = {
      ...applicantProfile,
      phone_number_when_move: formParam.phone_number_when_move,
      pay_method: parseInt(formParam.pay_method, 10),
      use_og_gas: parseInt(formParam.use_og_gas, 10),
      start_power_date: getDateString(formParam.start_power_date),
      start_gas_date: isCoodinatedGasStartDate
        ? undefined
        : getDateString(formParam.start_gas_date),
      start_gas_time: isCoodinatedGasStartDate
        ? undefined
        : formParam.start_gas_time
    };

    const occupant = {
      ...applicantProfile,
      bkp_user_id: user_id,
      usage_info_id: propParam.usageInfoId,
      // 旧名。takuty が people_id に切り替わるまでの二重送信
      // TODO: takuty #277 のデプロイ後に落とす
      persona_id: propParam.peopleId,
      people_id: propParam.peopleId,
      bkp_persona_id: bkpPersonaId
    };

    body = JSON.stringify({
      ...applicationTarget,
      ...(isOccupantUser ? {} : { contractor }),
      ...(isContractorUser ? {} : { occupant })
    });

    await fetch(endpoint, {
      method,
      headers,
      body,
      mode: 'cors'
    })
      .then(async res => {
        if (res.status === 200) {
          // 契約者のみはアクティベート対象が無い (カギを持たない)
          if (isContractorUser) {
            dispatch(Actions.successToSubmitParamater());
            return;
          }
          // 招待URLのクエリ由来で入居者経路なら必ず入るが、Property 上は任意なので絞り込む
          if (!propParam.peopleId) {
            dispatch(Actions.failedToSubmitParamater('internal'));
            return;
          }
          const activated = await activateOccupant({
            organizationId: activation.organizationId,
            peopleId: propParam.peopleId,
            usageId: propParam.usageId,
            accessToken: access_token
          });
          dispatch(
            activated
              ? Actions.successToSubmitParamater()
              : Actions.failedToSubmitParamater('internal')
          );
        } else if (res.status === 401) {
          dispatch(Actions.failedToSubmitParamater('unauthorized'));
        } else if (res.status === 409) {
          dispatch(Actions.failedToSubmitParamater('conflict'));
        } else {
          dispatch(Actions.failedToSubmitParamater('internal'));
        }
      })
      .catch(error => {
        console.error(error);
        dispatch(Actions.failedToSubmitParamater('internal'));
      });
  } catch (error) {
    console.error(error);
    dispatch(Actions.failedToSubmitParamater('internal'));
  }
};

const getDateString = (date?: string): string | undefined => {
  if (!date || date.length === 0) {
    return undefined;
  }
  return moment(new Date(date)).format('YYYY-MM-DD');
};
